// LLM generated - Model Claude 4 Sonnet
/**
 * HUMAN INTERVENTION
 * Simplified styling, added translations
 */
import { useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import Trans from 'next-translate/Trans';
import { Collapse, Group, Stack, Skeleton, Tooltip } from '@mantine/core';
import { IconChevronDown, IconInfoCircle } from '@tabler/icons-react';

import dt from '~/testing';
import { Button } from '~/domains/design-system/Button';
import { Text } from '~/domains/design-system/Text';
import { useIsMobile } from '~/domains/common/hooks/mediaQueries';

import useBalanceWidget from './useBalanceWidget';
import useStyles from './BalanceWidget.styles';

function BalanceRow({
  title,
  tooltip,
  amount,
  isLoading,
  isCompact,
}: {
  title: string;
  tooltip: string;
  amount: number;
  isLoading: boolean;
  isCompact: boolean;
}) {
  const { cx, classes } = useStyles();
  const { formatCurrency } = useBalanceWidget();
  const { dollars, cents } = formatCurrency(amount);
  const isMobile = useIsMobile();

  return (
    <Group
      data-test-id={dt.myAccount.components.balanceWidget.balanceRow}
      className={cx(classes.balanceRow, { isCompact })}
      position="apart"
    >
      <Group /* eslint-disable-line local-rules/use-theme-tokens */ spacing={8} align="center">
        <Text variant="title-medium" className={classes.balanceTitle}>
          {title}
        </Text>
        <Tooltip
          label={tooltip}
          events={{ hover: true, focus: false, touch: true }}
          withinPortal
          withArrow
          multiline={isMobile}
          classNames={{ tooltip: cx({ [classes.tooltip]: isMobile }) }}
        >
          <IconInfoCircle size={16} className={classes.infoIcon} />
        </Tooltip>
      </Group>

      <Group data-test-id={dt.myAccount.components.balanceWidget.balanceAmount} /* eslint-disable-line local-rules/use-theme-tokens */ spacing={2}>
        {isLoading ? (
          <Skeleton height={32} width={100} />
        ) : (
          <>
            <Text
              variant={isCompact ? 'subhead-large' : 'headline-xxl'}
              className={classes.dollarAmount}
            >
              ${dollars}
            </Text>
            <Text
              variant={isCompact ? 'subhead-small' : 'subhead-medium'}
              className={classes.centsAmount}
            >
              {cents}
            </Text>
          </>
        )}
      </Group>
    </Group>
  );
}

type BalanceWidgetProps = {
  cashBalance: number;
  withdrawableBalance: number;
  credits: number;
  refundableBalance?: number;
  playthroughCash?: number;
  onWithdraw: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  onDeposit: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  isLoadingWalletBalance?: boolean;
  isLoadingWalletAction?: boolean;
  isCompact?: boolean;
};

type CashBalanceRowProps = {
  cashBalance: number;
  withdrawableBalance: number;
  playthroughCash?: number;
  isLoading: boolean;
  isCompact: boolean;
};

function CashBalanceRow({
  cashBalance,
  withdrawableBalance,
  playthroughCash,
  isLoading,
  isCompact,
}: CashBalanceRowProps) {
  const { cx, classes } = useStyles();
  const { formatCurrency } = useBalanceWidget();
  const { t } = useTranslation('account');
  const isMobile = useIsMobile();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const { dollars, cents } = formatCurrency(cashBalance);
  const formattedWithdrawable = formatCurrency(withdrawableBalance);

  // Only show dropdown when there's actual playthrough cash > $0
  const hasPlaythroughCash = (playthroughCash ?? 0) > 0;
  const formattedPlaythrough = formatCurrency(playthroughCash ?? 0);

  const toggleDropdown = () => {
    if (hasPlaythroughCash) {
      setIsDropdownOpen((prev) => !prev);
    }
  };

  return (
    <div className={cx(classes.cashRow, { isCompact })}>
      <Group
        data-test-id={dt.myAccount.components.balanceWidget.balanceRow}
        className={cx(classes.balanceRow, { isCompact, noBorderTop: true })}
        position="apart"
        align="flex-start"
      >
        <Group /* eslint-disable-line local-rules/use-theme-tokens */ spacing={8} align="center" className={classes.cashLabelGroup}>
          <Text variant="title-medium" className={classes.balanceTitle}>
            {t('balanceWidget.cash')}
          </Text>
          <Tooltip
            label={t('balanceWidget.cashTooltip')}
            events={{ hover: true, focus: false, touch: true }}
            withinPortal
            withArrow
            multiline={isMobile}
            classNames={{ tooltip: cx({ [classes.tooltip]: isMobile }) }}
          >
            <IconInfoCircle size={16} className={classes.infoIcon} />
          </Tooltip>
        </Group>

        <div className={classes.cashAmountColumn}>
          <Group data-test-id={dt.myAccount.components.balanceWidget.balanceAmount} /* eslint-disable-line local-rules/use-theme-tokens */ spacing={2}>
            {isLoading ? (
              <Skeleton height={32} width={100} />
            ) : (
              <>
                <Text
                  variant={isCompact ? 'subhead-large' : 'headline-xxl'}
                  className={classes.dollarAmount}
                >
                  ${dollars}
                </Text>
                <Text
                  variant={isCompact ? 'subhead-small' : 'subhead-medium'}
                  className={classes.centsAmount}
                >
                  {cents}
                </Text>
              </>
            )}
          </Group>

          {/* Withdrawable sub-text with dropdown chevron - only show when there's playthrough cash */}
          {!isLoading && hasPlaythroughCash && (
            <button type="button" className={classes.withdrawableSubtext} onClick={toggleDropdown}>
              <Text variant="body-small" component="span" className={classes.withdrawableText}>
                {t('balanceWidget.withdrawableAmount', {
                  amount: `$${formattedWithdrawable.dollars}.${formattedWithdrawable.cents}`,
                })}
              </Text>
              <IconChevronDown
                size={14}
                className={cx(classes.dropdownChevron, { isOpen: isDropdownOpen })}
              />
            </button>
          )}
        </div>
      </Group>

      {/* Playthrough explanation dropdown */}
      {hasPlaythroughCash && (
        <Collapse in={isDropdownOpen}>
          <div className={classes.playthroughDropdown}>
            <Text variant="body-large" color="gray2.6">
              <Trans
                i18nKey="account:balanceWidget.playthroughExplanation"
                values={{
                  amount: `$${formattedPlaythrough.dollars}.${formattedPlaythrough.cents}`,
                }}
                components={{
                  bold: <Text component="span" variant="title-medium" />,
                }}
              />
            </Text>
          </div>
        </Collapse>
      )}
    </div>
  );
}

function BalanceWidget({
  cashBalance,
  withdrawableBalance,
  credits,
  refundableBalance,
  playthroughCash,
  onWithdraw,
  onDeposit,
  isLoadingWalletBalance,
  isLoadingWalletAction,
  isCompact,
}: BalanceWidgetProps) {
  const { cx, classes } = useStyles();
  const { t } = useTranslation('account');

  return (
    <div className={cx(classes.container, { isCompact })}>
      <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={isCompact ? 12 : 16}>
        <Stack spacing={0}>
          {/* Primary: Cash balance with withdrawable sub-text and dropdown */}
          <CashBalanceRow
            cashBalance={cashBalance}
            withdrawableBalance={withdrawableBalance}
            playthroughCash={playthroughCash}
            isLoading={isLoadingWalletBalance}
            isCompact={isCompact}
          />

          {/* Refundable balance (shown only when > 0) */}
          {refundableBalance > 0 && (
            <BalanceRow
              title={t('balanceWidget.refundableBalance')}
              tooltip={t('balanceWidget.refundableTooltip')}
              amount={refundableBalance}
              isLoading={isLoadingWalletBalance}
              isCompact={isCompact}
            />
          )}

          {/* Credits */}
          <BalanceRow
            title={t('balanceWidget.credits')}
            tooltip={t('balanceWidget.creditsTooltip')}
            amount={credits}
            isLoading={isLoadingWalletBalance}
            isCompact={isCompact}
          />
        </Stack>

        <Group /* eslint-disable-line local-rules/use-theme-tokens */ spacing={16} noWrap>
          <Button
            data-test-id={dt.common.components.appShellHeader.userMenu.withdrawButton}
            variant="tertiary-fill"
            size="small"
            loading={isLoadingWalletAction}
            onClick={onWithdraw}
            fullWidth
          >
            {t('balanceWidget.withdraw')}
          </Button>

          <Button
            data-test-id={dt.common.components.appShellHeader.userMenu.depositButton}
            variant="primary-fill"
            size="small"
            loading={isLoadingWalletAction}
            onClick={onDeposit}
            fullWidth
          >
            {t('balanceWidget.deposit')}
          </Button>
        </Group>
      </Stack>
    </div>
  );
}

export default BalanceWidget;
